// Rate Integration Utilities for Calculator Integration
// This file provides functions to get interest rates from the Redux store
// and integrate them with the existing calculator logic

import { type RootState } from '@/store';

import type { HybridBackendRates } from '@/store/slices/hybridInterestRateSlice';
import { LOAN_TYPE_TO_PROGRAM_KEY, priceProgram } from '@/utils/rateEngine';
import { type LoanType } from '@/utils/rateMatrixUtils';

const loanTypeToBackendKey: Record<LoanType, keyof HybridBackendRates> = {
  // Cash rate is 0; key unused (see getDefaultRateForLoanType)
  Cash: 'conventional',
  Conventional: 'conventional',
  FHA: 'fha',
  VA: 'va',
  USDA: 'usda',
  DSCR: 'dscr',
  Commercial: 'commercial',
  FixAndFlip: 'fixAndFlip',
  Bridge: 'bridge',
  HELOC: 'heloc',
  HELOAN: 'heloan',
  SecondMortgage: 'secondMortgage',
};

// Cache for rate lookups to reduce logging
let lastRateLookup: {
  loanType: LoanType;
  creditScore: number;
  ltv: number;
  rate: number;
  timestamp: number;
} | null = null;

// Cache for matrix rate lookups to reduce excessive logging
let lastMatrixLookup: {
  creditScore: number;
  ltv: number;
  rate: number;
  creditScoreIndex: number;
  ltvIndex: number;
  timestamp: number;
} | null = null;

// Flag to enable/disable matrix lookup logging (set to false to disable completely)
const ENABLE_MATRIX_LOOKUP_LOGGING = false; // Disabled by default to reduce console noise

// Function to get the current interest rate from Redux store
export const getCurrentInterestRate = (
  state: RootState,
  loanType: LoanType,
  creditScore: number,
  ltv: number,
  loanTerm?: number,
  /** Extra grid inputs, e.g. the borrower's DTI for FHA/VA/USDA. */
  pricing?: { dti?: number | null; dscr?: number | null }
): number => {
  if (loanType === 'Cash') {
    return 0;
  }

  // Primary path: the backend's live rate sheet (daily market base rate +
  // today's admin grid), priced by the same engine the backend quotes with.
  const rateSheet = state.hybridInterestRate?.rateSheet;
  const programKey = LOAN_TYPE_TO_PROGRAM_KEY[loanType];
  if (rateSheet && programKey) {
    const priced = priceProgram(rateSheet, programKey, {
      creditScore,
      ltv,
      loanTerm,
      dti: pricing?.dti,
      dscr: pricing?.dscr,
    });
    if (priced && Number.isFinite(priced.rate)) {
      return priced.rate;
    }
  }

  // Fallback (rate sheet not loaded yet): admin-page matrices, then base rate.

  const rateMatrices = state.interestRate.rateMatrices;
  const matricesLoaded = state.interestRate.matricesLoaded;
  const matrix = matricesLoaded ? rateMatrices[loanType] : null;

  let rate: number;

  if (!matrix) {
    // Fallback to backend rates if matrix not found
    rate = getDefaultRateForLoanType(state, loanType);
  } else {
    // Find the appropriate rate based on credit score and LTV
    const matrixRate = findRateFromMatrix(matrix, creditScore, ltv);
    rate =
      matrixRate != null && Number.isFinite(matrixRate)
        ? matrixRate
        : getDefaultRateForLoanType(state, loanType);
  }

  // Only log if enabled and if the rate or inputs have changed significantly
  const ENABLE_RATE_LOOKUP_LOGGING = false; // Disabled to reduce console noise

  if (ENABLE_RATE_LOOKUP_LOGGING) {
    const now = Date.now();
    const shouldLog =
      !lastRateLookup ||
      lastRateLookup.loanType !== loanType ||
      Math.abs(lastRateLookup.creditScore - creditScore) > 10 ||
      Math.abs(lastRateLookup.ltv - ltv) > 0.05 ||
      Math.abs(lastRateLookup.rate - rate) > 0.1 ||
      now - lastRateLookup.timestamp > 30000; // Log at most every 30 seconds

    if (shouldLog) {
      console.log('[ProductDetail Rate Lookup]', {
        loanType,
        creditScore,
        ltv: (ltv * 100).toFixed(2) + '%',
        rate,
        source: matrix ? 'matrix' : 'backend',
        timestamp: new Date().toISOString(),
      });
      lastRateLookup = { loanType, creditScore, ltv, rate, timestamp: now };
    }
  } else {
    // Still update cache even if not logging
    const now = Date.now();
    if (
      !lastRateLookup ||
      lastRateLookup.loanType !== loanType ||
      Math.abs(lastRateLookup.creditScore - creditScore) > 5 ||
      Math.abs(lastRateLookup.ltv - ltv) > 0.01 ||
      Math.abs(lastRateLookup.rate - rate) > 0.01
    ) {
      lastRateLookup = { loanType, creditScore, ltv, rate, timestamp: now };
    }
  }

  return Number.isFinite(rate)
    ? rate
    : getDefaultRateForLoanType(state, loanType);
};

// Helper function to find rate from matrix
const findRateFromMatrix = (
  matrix: any,
  creditScore: number,
  ltv: number
): number | null => {
  if (!matrix.matrix || !Array.isArray(matrix.matrix)) {
    return null;
  }

  // Find the closest credit score and LTV ranges
  const config = matrix.config;
  if (!config) {
    return null;
  }

  // Helper to parse numeric value from range string
  // Examples: "760+" -> 760, "700-759" -> 700, "<650" -> 650
  const parseMinValue = (rangeStr: string): number => {
    const match = rangeStr.match(/(\d+)/);
    return match ? parseInt(match[1], 10) : -1;
  };

  // Helper to parse max value from range string
  // Examples: "700-759" -> 759, "≤60%" -> 60, "760+" -> Infinity
  const parseMaxValue = (rangeStr: string): number => {
    if (rangeStr.includes('+')) {
      return Infinity; // "760+" means 760 and above
    }
    if (rangeStr.includes('≤')) {
      const match = rangeStr.match(/≤(\d+)/);
      return match ? parseInt(match[1], 10) : -1;
    }
    if (rangeStr.includes('-')) {
      const parts = rangeStr.split('-');
      if (parts.length === 2) {
        const match = parts[1].match(/(\d+)/);
        return match ? parseInt(match[1], 10) : -1;
      }
    }
    // Try to extract any number as fallback
    const match = rangeStr.match(/(\d+)/);
    return match ? parseInt(match[1], 10) : -1;
  };

  // Find credit score index by checking which range the score falls into
  let creditScoreIndex = -1;
  if (config.xAxis.ranges && Array.isArray(config.xAxis.ranges)) {
    for (let i = 0; i < config.xAxis.ranges.length; i++) {
      const rangeStr = config.xAxis.ranges[i];
      const minValue = parseMinValue(rangeStr);
      const maxValue = parseMaxValue(rangeStr);

      if (rangeStr.includes('+')) {
        // "760+" means >= 760
        if (creditScore >= minValue) {
          creditScoreIndex = i;
          break;
        }
      } else if (rangeStr.includes('<')) {
        // "<650" means < 650
        if (creditScore < minValue) {
          creditScoreIndex = i;
          break;
        }
      } else if (rangeStr.includes('-')) {
        // "700-759" means >= 700 and <= 759
        if (creditScore >= minValue && creditScore <= maxValue) {
          creditScoreIndex = i;
          break;
        }
      }
    }
  }

  // Find LTV index by checking which range the LTV falls into
  // Note: LTV is a decimal (0.8 = 80%), so we need to convert to percentage for comparison
  const ltvPercent = ltv * 100;
  let ltvIndex = -1;
  if (config.yAxis.ranges && Array.isArray(config.yAxis.ranges)) {
    for (let i = 0; i < config.yAxis.ranges.length; i++) {
      const rangeStr = config.yAxis.ranges[i];
      const minValue = parseMinValue(rangeStr);
      const maxValue = parseMaxValue(rangeStr);

      if (rangeStr.includes('+')) {
        // "91%+" means >= 91%
        if (ltvPercent >= minValue) {
          ltvIndex = i;
          break;
        }
      } else if (rangeStr.includes('<')) {
        // "<650" means < 650 (unlikely for LTV, but handle it)
        if (ltvPercent < minValue) {
          ltvIndex = i;
          break;
        }
      } else if (rangeStr.includes('-')) {
        // "61-80%" means >= 61% and <= 80%
        if (ltvPercent >= minValue && ltvPercent <= maxValue) {
          ltvIndex = i;
          break;
        }
      } else if (rangeStr.includes('≤')) {
        // "≤60%" means <= 60%
        if (ltvPercent <= maxValue) {
          ltvIndex = i;
          break;
        }
      }
    }
  }

  // Get rate from matrix
  if (
    creditScoreIndex >= 0 &&
    ltvIndex >= 0 &&
    matrix.matrix[ltvIndex] &&
    matrix.matrix[ltvIndex][creditScoreIndex]
  ) {
    const rate = matrix.matrix[ltvIndex][creditScoreIndex].rate;

    // Build a display of all rates for the matched credit score bracket (entire row)
    const creditScoreRowRates: Record<string, number> = {};
    if (matrix.matrix[ltvIndex] && config.yAxis.ranges) {
      config.yAxis.ranges.forEach((ltvRange: string, idx: number) => {
        if (matrix.matrix[idx] && matrix.matrix[idx][creditScoreIndex]) {
          creditScoreRowRates[ltvRange] =
            matrix.matrix[idx][creditScoreIndex].rate;
        }
      });
    }

    // Build a display of all rates for the matched LTV bracket (entire column)
    const ltvColumnRates: Record<string, number> = {};
    if (matrix.matrix[ltvIndex] && config.xAxis.ranges) {
      config.xAxis.ranges.forEach((creditRange: string, idx: number) => {
        if (matrix.matrix[ltvIndex][idx]) {
          ltvColumnRates[creditRange] = matrix.matrix[ltvIndex][idx].rate;
        }
      });
    }

    // Build full matrix view for reference
    const fullMatrix: Record<string, Record<string, number>> = {};
    if (config.xAxis.ranges && config.yAxis.ranges) {
      config.yAxis.ranges.forEach((ltvRange: string, ltvIdx: number) => {
        fullMatrix[ltvRange] = {};
        config.xAxis.ranges.forEach(
          (creditRange: string, creditIdx: number) => {
            if (matrix.matrix[ltvIdx] && matrix.matrix[ltvIdx][creditIdx]) {
              fullMatrix[ltvRange][creditRange] =
                matrix.matrix[ltvIdx][creditIdx].rate;
            }
          }
        );
      });
    }

    // Only log if enabled and if the lookup parameters or result have changed significantly
    if (ENABLE_MATRIX_LOOKUP_LOGGING) {
      const now = Date.now();
      const shouldLog =
        !lastMatrixLookup ||
        Math.abs(lastMatrixLookup.creditScore - creditScore) > 10 ||
        Math.abs(lastMatrixLookup.ltv - ltv) > 0.05 ||
        Math.abs(lastMatrixLookup.rate - rate) > 0.1 ||
        lastMatrixLookup.creditScoreIndex !== creditScoreIndex ||
        lastMatrixLookup.ltvIndex !== ltvIndex ||
        now - lastMatrixLookup.timestamp > 30000; // Log at most every 30 seconds

      if (shouldLog) {
        console.log(
          '[Matrix Rate Lookup] Found rate from admin portal matrix',
          {
            input: {
              creditScore,
              ltv: (ltv * 100).toFixed(2) + '%',
            },
            matchedBrackets: {
              creditScoreRange: config.xAxis.ranges?.[creditScoreIndex],
              ltvRange: config.yAxis.ranges?.[ltvIndex],
              creditScoreIndex,
              ltvIndex,
            },
            selectedRate: rate,
            allRatesForCreditScoreBracket: {
              bracket: config.xAxis.ranges?.[creditScoreIndex],
              ratesByLTV: creditScoreRowRates,
            },
            allRatesForLTVBracket: {
              bracket: config.yAxis.ranges?.[ltvIndex],
              ratesByCreditScore: ltvColumnRates,
            },
            fullMatrix: fullMatrix,
            source: 'admin-portal-matrix',
          }
        );
        lastMatrixLookup = {
          creditScore,
          ltv,
          rate,
          creditScoreIndex,
          ltvIndex,
          timestamp: now,
        };
      }
    } else {
      // Still update cache even if not logging
      const now = Date.now();
      if (
        !lastMatrixLookup ||
        Math.abs(lastMatrixLookup.creditScore - creditScore) > 5 ||
        Math.abs(lastMatrixLookup.ltv - ltv) > 0.01 ||
        Math.abs(lastMatrixLookup.rate - rate) > 0.01
      ) {
        lastMatrixLookup = {
          creditScore,
          ltv,
          rate,
          creditScoreIndex,
          ltvIndex,
          timestamp: now,
        };
      }
    }

    return rate;
  }

  // Only log errors if enabled and if they haven't been logged recently
  if (ENABLE_MATRIX_LOOKUP_LOGGING) {
    const now = Date.now();
    const shouldLogError =
      !lastMatrixLookup ||
      Math.abs(lastMatrixLookup.creditScore - creditScore) > 10 ||
      Math.abs(lastMatrixLookup.ltv - ltv) > 0.05 ||
      now - lastMatrixLookup.timestamp > 30000; // Log at most every 30 seconds

    if (shouldLogError) {
      console.warn('[Matrix Rate Lookup] No rate found in matrix', {
        creditScore,
        ltv: (ltv * 100).toFixed(2) + '%',
        creditScoreIndex,
        ltvIndex,
        hasMatrix: !!matrix.matrix,
        matrixLength: matrix.matrix?.length,
        configRanges: {
          creditScore: config.xAxis.ranges,
          ltv: config.yAxis.ranges,
        },
      });
      lastMatrixLookup = {
        creditScore,
        ltv,
        rate: 0,
        creditScoreIndex,
        ltvIndex,
        timestamp: now,
      };
    }
  }

  return null;
};

// Default rates for each loan type (fallback)
const getDefaultRateForLoanType = (
  state: RootState,
  loanType: LoanType
): number => {
  if (loanType === 'Cash') {
    return 0;
  }
  const backendRates = state.hybridInterestRate?.backendRates;
  const backendKey = loanTypeToBackendKey[loanType];

  if (backendRates && backendKey) {
    const candidate = backendRates[backendKey];
    if (typeof candidate === 'number' && Number.isFinite(candidate)) {
      return candidate;
    }
  }

  // Production: no fabricated rates (Phase A). Dev-only fallback for local work without API.
  if (!import.meta.env.DEV) {
    return Number.NaN;
  }

  const defaultRates: Record<LoanType, number> = {
    Cash: 0,
    Conventional: 6.75,
    FHA: 6.5,
    VA: 6.25,
    USDA: 6.0,
    DSCR: 7.5,
    Commercial: 8.0,
    FixAndFlip: 9.0,
    Bridge: 10.5,
    HELOC: 7.75,
    HELOAN: 7.25,
    SecondMortgage: 8.75,
  };

  const fallbackRate = defaultRates[loanType] || 6.75;
  // Only warn once per session if using fallback
  if (!(window as any).__rateFallbackWarned) {
    console.warn(
      '[ProductDetail Rate Lookup] Using hard-coded fallback rate - backend rates not available',
      {
        loanType,
        rate: fallbackRate,
        source: 'hardcoded-fallback',
        warning: 'Check API connection and backend rate configuration',
      }
    );
    (window as any).__rateFallbackWarned = true;
  }
  return fallbackRate;
};

// Enhanced interest rate calculation that uses the Redux store
export const calculateEnhancedInterestRate = (
  state: RootState,
  loanType: LoanType,
  baseRate: number,
  creditScore: number,
  ltv: number,
  loanTerm?: number,
  pricing?: { dti?: number | null; dscr?: number | null }
): number => {
  // Defensive: if called without a valid Redux state or inputs, fall back safely
  if (
    !state ||
    !(state as any).interestRate ||
    !(state as any).interestRate.rateMatrices ||
    !Number.isFinite(creditScore) ||
    !Number.isFinite(ltv)
  ) {
    const safeCreditScore = Number.isFinite(creditScore) ? creditScore : 700;
    const safeLtv = Number.isFinite(ltv) ? ltv : 0.8;
    return calculateFallbackInterestRate(
      loanType,
      baseRate,
      safeCreditScore,
      safeLtv
    );
  }

  if (loanType === 'Cash') {
    return 0;
  }

  if (state.hybridInterestRate?.rateSheet) {
    const sheetRate = getCurrentInterestRate(
      state,
      loanType,
      creditScore,
      ltv,
      loanTerm,
      pricing
    );
    if (Number.isFinite(sheetRate)) return sheetRate;
  }

  // Always try to get rate from the interest rate system first (backend/admin controlled)
  const rateMatrices = state.interestRate.rateMatrices;
  const matricesLoaded = state.interestRate.matricesLoaded;
  const matrix = matricesLoaded ? rateMatrices[loanType] : null;

  // Check if we have a matrix configured (admin has set up rates)
  const hasMatrix =
    matricesLoaded &&
    matrix &&
    matrix.matrix &&
    Array.isArray(matrix.matrix) &&
    matrix.matrix.length > 0;

  // Check if we have backend rates available
  const backendRates = state.hybridInterestRate?.backendRates;
  const backendKey = loanTypeToBackendKey[loanType];
  const hasBackendRate =
    backendRates && backendKey && typeof backendRates[backendKey] === 'number';

  // If we have either matrix or backend rates, use the system rate (backend controls rates)
  if (hasMatrix || hasBackendRate) {
    const systemRate = getCurrentInterestRate(
      state,
      loanType,
      creditScore,
      ltv,
      loanTerm,
      pricing
    );
    if (Number.isFinite(systemRate)) {
      return systemRate;
    }
  }

  // Only apply fallback adjustments if we truly don't have backend data
  // This should rarely happen if backend is properly configured
  return calculateFallbackInterestRate(loanType, baseRate, creditScore, ltv);
};

// Fallback calculation logic (original calculator logic)
const calculateFallbackInterestRate = (
  loanType: LoanType,
  baseRate: number,
  creditScore: number,
  ltv: number
): number => {
  let rateAdjustment = 0;

  // Credit score adjustments
  if (creditScore >= 760) {
    rateAdjustment -= 0.25;
  } else if (creditScore >= 740) {
    rateAdjustment -= 0.125;
  } else if (creditScore >= 720) {
    rateAdjustment -= 0.0625;
  } else if (creditScore >= 700) {
    // No change
  } else if (creditScore >= 680) {
    rateAdjustment += 0.125;
  } else if (creditScore >= 660) {
    rateAdjustment += 0.25;
  } else if (creditScore >= 640) {
    rateAdjustment += 0.375;
  } else if (creditScore >= 620) {
    rateAdjustment += 0.5;
  } else if (creditScore >= 600) {
    rateAdjustment += 0.625;
  } else if (creditScore >= 580) {
    rateAdjustment += 0.75;
  } else if (creditScore >= 560) {
    rateAdjustment += 0.875;
  } else if (creditScore >= 540) {
    rateAdjustment += 1.0;
  } else if (creditScore >= 520) {
    rateAdjustment += 1.125;
  } else {
    rateAdjustment += 1.25;
  }

  // LTV adjustments
  if (ltv > 0.95) {
    rateAdjustment += 0.375;
  } else if (ltv > 0.9) {
    rateAdjustment += 0.25;
  } else if (ltv > 0.85) {
    rateAdjustment += 0.125;
  } else if (ltv > 0.8) {
    rateAdjustment += 0.0625;
  } else {
    // LTV <= 80%, no additional adjustment
  }

  return Math.max(0, baseRate + rateAdjustment);
};

// Get Bridge rate by term (6, 12, 18, 24 months)
export const getBridgeRateByTerm = (
  state: RootState,
  loanTerm: number,
  creditScore?: number,
  ltv?: number
): number => {
  const bridgeRatesByTerm = state.hybridInterestRate?.bridgeRatesByTerm;
  const baseBridgeRate = state.hybridInterestRate?.backendRates?.bridge;
  const safeCreditScore = Number.isFinite(creditScore)
    ? (creditScore as number)
    : 700;
  const safeLtv = Number.isFinite(ltv) ? (ltv as number) : 0.8;

  if (!bridgeRatesByTerm) {
    return getCurrentInterestRate(state, 'Bridge', safeCreditScore, safeLtv);
  }

  const termRate = (() => {
    switch (loanTerm) {
      case 6:
        return bridgeRatesByTerm.sixMonth;
      case 12:
        return bridgeRatesByTerm.twelveMonth;
      case 18:
        return bridgeRatesByTerm.eighteenMonth;
      case 24:
        return bridgeRatesByTerm.twentyFourMonth;
      default:
        return bridgeRatesByTerm.twelveMonth;
    }
  })();

  const matrixRate = getCurrentInterestRate(
    state,
    'Bridge',
    safeCreditScore,
    safeLtv
  );

  if (typeof baseBridgeRate === 'number' && Number.isFinite(baseBridgeRate)) {
    const termOffset = termRate - baseBridgeRate;
    return matrixRate + termOffset;
  }

  return Number.isFinite(matrixRate) ? matrixRate : termRate;
};
